import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import {
  FiX,
  FiMessageSquare,
  FiAlertTriangle,
  FiCheck,
  FiEdit2,
  FiSave,
  FiPhone,
  FiMail,
  FiUser,
  FiUsers,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiAlertOctagon,
  FiCopy,
  FiFileText,
  FiNavigation,
} from "react-icons/fi";
import { api } from "../../services/api";
import { PageTitle } from "../../components/PageTitle";
import { useAuth } from "../../contexts/AuthContext";
import { Modal, ModalButton } from "../../components/Modal";
import { maskZIP } from "../../components/masks/Masks";
import {
  BigNumbersSection,
  ReportTypeToggle,
  FilterControls,
  MapLegend,
} from "./components";

interface Costumer {
  id: string;
  name: string;
  address: string;
  number: string;
  complement?: string;
  district: string;
  city: string;
  state: string;
  zip?: string;
  latitude: number;
  longitude: number;
  emergency: boolean;
  days_without_report: number | null;
  total_reports: number;
  is_delivery_only: boolean;
  last_report_id: string | null;
  alert_days_without_report?: number;
  zone?: {
    id: string;
    name: string;
    color?: string;
  };
}

interface Responsible {
  id: string;
  name: string;
  email: string;
  phone: number;
  status: string;
}

interface Zone {
  id: string;
  name: string;
  status: string;
  color?: string;
}

interface Observation {
  id: string;
  text: string;
  status: "open" | "closed";
  opened_at: string;
  closed_at: string | null;
  user: {
    id: string;
    name: string;
  };
}

type ViewMode = "zones" | "monitoring" | "emergency";
type PinTheme = "number" | "name" | "fullName";
type MapBackground = "streets" | "satellite";
type MonitoringStatus = "ok" | "attention" | "alert" | "critical" | "delivery_only";

const MONITORING_STATUS_OPTIONS: { value: MonitoringStatus; label: string; color: string; bgColor: string }[] = [
  { value: "ok", label: "0-15 dias (OK)", color: "#22C55E", bgColor: "#DCFCE7" },
  { value: "attention", label: "16-30 dias (Atenção)", color: "#EAB308", bgColor: "#FEF9C3" },
  { value: "alert", label: "31-45 dias (Alerta)", color: "#F97316", bgColor: "#FFEDD5" },
  { value: "critical", label: "+46 dias (Crítico)", color: "#EF4444", bgColor: "#FEE2E2" },
  { value: "delivery_only", label: "Apenas entregas", color: "#6B7280", bgColor: "#F3F4F6" },
];

function getMonitoringStatus(costumer: Costumer): MonitoringStatus {
  if (costumer.is_delivery_only) return "delivery_only";
  if (costumer.days_without_report === null) return "critical";
  if (costumer.days_without_report <= 15) return "ok";
  if (costumer.days_without_report <= 30) return "attention";
  if (costumer.days_without_report <= 45) return "alert";
  return "critical";
}

interface MapComponentProps {
  costumers: Costumer[];
  selectedZones: string[];
  selectedStatuses: MonitoringStatus[];
  viewMode: ViewMode;
  pinTheme: PinTheme;
  mapBackground: MapBackground;
  reportType: "technical_visit" | "delivery";
  onCostumerClick?: (costumer: Costumer) => void;
}

interface SidePanelProps {
  costumer: Costumer | null;
  zones: Zone[];
  onClose: () => void;
  onObservationCreated: () => void;
  onCostumerUpdated: () => void;
  isMobile?: boolean;
}

// Componente do Painel Lateral
function SidePanel({
  costumer,
  zones,
  onClose,
  onObservationCreated,
  onCostumerUpdated,
  isMobile = false,
}: SidePanelProps) {
  const [observations, setObservations] = useState<Observation[]>([]);
  const [responsibles, setResponsibles] = useState<Responsible[]>([]);
  const [newObservation, setNewObservation] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingResponsibles, setLoadingResponsibles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savingCostumer, setSavingCostumer] = useState(false);
  const [showObservations, setShowObservations] = useState(false);
  const [showResponsibles, setShowResponsibles] = useState(false);
  const [confirmCloseModal, setConfirmCloseModal] = useState<{
    isOpen: boolean;
    observationId: string;
  }>({ isOpen: false, observationId: "" });
  const [closingObservation, setClosingObservation] = useState(false);

  // Estado para edição do cliente
  const [editForm, setEditForm] = useState({
    name: "",
    address: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    zip: "",
    zone_id: "",
    alert_days_without_report: 0,
  });

  // Carrega observações
  const loadObservations = useCallback(async () => {
    if (!costumer) return;
    setLoading(true);
    try {
      const response = await api.get(`/costumer-observations/${costumer.id}`);
      setObservations(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao carregar observações:", error);
    } finally {
      setLoading(false);
    }
  }, [costumer]);

  // Carrega responsáveis
  const loadResponsibles = useCallback(async () => {
    if (!costumer) return;
    setLoadingResponsibles(true);
    try {
      const response = await api.get(
        `/responsibles?costumer_id=${costumer.id}&status=A`,
      );
      setResponsibles(response.data.data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao carregar responsáveis:", error);
    } finally {
      setLoadingResponsibles(false);
    }
  }, [costumer]);

  useEffect(() => {
    if (costumer) {
      loadObservations();
      loadResponsibles();
      // Inicializa formulário de edição
      setEditForm({
        name: costumer.name || "",
        address: costumer.address || "",
        number: costumer.number || "",
        complement: costumer.complement || "",
        district: costumer.district || "",
        city: costumer.city || "",
        state: costumer.state || "",
        zip: maskZIP(costumer.zip || ""),
        zone_id: costumer.zone?.id || "",
        alert_days_without_report: costumer.alert_days_without_report || 0,
      });
      setIsEditing(false);
    }
  }, [costumer, loadObservations, loadResponsibles]);

  const handleCreateObservation = async () => {
    if (!costumer || !newObservation.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/costumer-observations", {
        costumer_id: costumer.id,
        text: newObservation.trim(),
      });
      setNewObservation("");
      loadObservations();
      onObservationCreated();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao criar observação:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const openCloseConfirmation = (observationId: string) => {
    setConfirmCloseModal({ isOpen: true, observationId });
  };

  const handleCloseObservation = async () => {
    if (!costumer || !confirmCloseModal.observationId) return;
    setClosingObservation(true);
    try {
      await api.patch("/costumer-observations/close", {
        observation_ids: [confirmCloseModal.observationId],
        costumer_id: costumer.id,
      });
      setConfirmCloseModal({ isOpen: false, observationId: "" });
      loadObservations();
      onObservationCreated();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao fechar observação:", error);
    } finally {
      setClosingObservation(false);
    }
  };

  const handleSaveCostumer = async () => {
    if (!costumer) return;
    setSavingCostumer(true);
    try {
      // Remove máscara do CEP para comparar e salvar
      const cleanZip = editForm.zip.replace(/\D/g, "");
      const originalCleanZip = (costumer.zip || "").replace(/\D/g, "");
      const zipChanged = cleanZip !== originalCleanZip;

      // Envia os dados com CEP limpo (sem máscara)
      const dataToSave = { ...editForm, zip: cleanZip };
      await api.put(`/costumers/${costumer.id}`, dataToSave);

      // Se o CEP mudou, atualiza as coordenadas do cliente
      if (zipChanged && cleanZip.length === 8) {
        try {
          await api.post("/costumers/geocode", { costumer_id: costumer.id });
        } catch (geocodeError) {
          // eslint-disable-next-line no-console
          console.error("Erro ao geocodificar cliente:", geocodeError);
        }
      }

      setIsEditing(false);
      onCostumerUpdated();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao salvar cliente:", error);
    } finally {
      setSavingCostumer(false);
    }
  };

  const handleCancelEdit = () => {
    if (costumer) {
      setEditForm({
        name: costumer.name || "",
        address: costumer.address || "",
        number: costumer.number || "",
        complement: costumer.complement || "",
        district: costumer.district || "",
        city: costumer.city || "",
        state: costumer.state || "",
        zip: maskZIP(costumer.zip || ""),
        zone_id: costumer.zone?.id || "",
        alert_days_without_report: costumer.alert_days_without_report || 0,
      });
    }
    setIsEditing(false);
  };

  // Busca endereço pelo CEP via ViaCEP
  const handleZipChange = async (value: string) => {
    // Aplica a máscara e atualiza o estado
    const masked = maskZIP(value);
    setEditForm(prev => ({ ...prev, zip: masked }));

    // Remove caracteres não numéricos para buscar
    const cleanZip = value.replace(/\D/g, "");

    // Busca endereço se o CEP tiver 8 dígitos
    if (cleanZip.length === 8) {
      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${cleanZip}/json/`,
        );
        if (!response.data.erro) {
          setEditForm(prev => ({
            ...prev,
            address: response.data.logradouro || prev.address,
            district: response.data.bairro || prev.district,
            city: response.data.localidade || prev.city,
            state: response.data.uf || prev.state,
          }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  if (!costumer) return null;

  // Classes condicionais para mobile/desktop
  const panelClasses = isMobile
    ? "w-full bg-white shadow-lg rounded-t-xl max-h-[70vh] overflow-y-auto"
    : "fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-[1000] overflow-y-auto animate-slide-in-right";

  return (
    <>
      {/* Estilo para animação de slide */}
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .animate-slide-in-right {
            animation: slideInRight 0.3s ease-out forwards;
          }
        `}
      </style>
      <div className={panelClasses}>
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white p-4 z-10 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold truncate flex-1">{costumer.name}</h2>
          <div className="flex items-center gap-1">
            {!isEditing ? (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 text-sm font-medium backdrop-blur-sm border border-white/30"
                title="Editar cliente"
              >
                <FiEdit2 size={16} />
                <span className="hidden sm:inline">Editar</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSaveCostumer}
                  disabled={savingCostumer}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-400 rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Salvar"
                >
                  <FiSave size={16} />
                  <span className="hidden sm:inline">
                    {savingCostumer ? "Salvando..." : "Salvar"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                  title="Cancelar"
                >
                  <FiX size={16} />
                  <span className="hidden sm:inline">Cancelar</span>
                </button>
              </>
            )}
            {!isEditing && (
              <button
                type="button"
                onClick={onClose}
                className="ml-1 p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                title="Fechar"
              >
                <FiX size={22} />
              </button>
            )}
          </div>
        </div>
        {costumer.emergency && (
          <div className="mt-3 flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg animate-pulse border border-red-400/50">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
            </span>
            <FiAlertTriangle size={18} className="animate-bounce" />
            <span>Emergência Ativa</span>
          </div>
        )}
      </div>

      {/* Dados do Cliente (editável) */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-700 flex items-center gap-2">
            <FiUser className="text-[#0085BD]" /> Dados do Cliente
          </h3>
          <button
            type="button"
            title="Copiar endereço"
            onClick={() => {
              const addressParts = [
                costumer.address,
                costumer.number,
                costumer.district,
                costumer.city,
                costumer.state,
              ].filter(Boolean);
              const fullAddress = addressParts.join(", ");
              navigator.clipboard.writeText(fullAddress).then(() => {
                enqueueSnackbar("Endereço copiado!", { variant: "success" });
              });
            }}
            className="flex items-center gap-1 px-2 py-1 text-gray-400 hover:text-[#0085BD] hover:bg-gray-100 rounded-lg transition-all duration-200 text-xs"
          >
            <FiCopy size={14} />
            <span>Copiar endereço</span>
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <span className="text-xs text-gray-500 block mb-1">Nome</span>
            <input
              type="text"
              value={editForm.name}
              onChange={e => setEditForm({ ...editForm, name: e.target.value })}
              disabled={!isEditing}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${
                isEditing
                  ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <span className="text-xs text-gray-500 block mb-1">Endereço</span>
              <input
                type="text"
                value={editForm.address}
                onChange={e =>
                  setEditForm({ ...editForm, address: e.target.value })
                }
                disabled={!isEditing}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isEditing
                    ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              />
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">Nº</span>
              <input
                type="text"
                value={editForm.number}
                onChange={e =>
                  setEditForm({ ...editForm, number: e.target.value })
                }
                disabled={!isEditing}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isEditing
                    ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500 block mb-1">Bairro</span>
              <input
                type="text"
                value={editForm.district}
                onChange={e =>
                  setEditForm({ ...editForm, district: e.target.value })
                }
                disabled={!isEditing}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isEditing
                    ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              />
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">
                Cidade/UF
              </span>
              <div className="flex gap-1 overflow-hidden">
                <input
                  type="text"
                  value={editForm.city}
                  onChange={e =>
                    setEditForm({ ...editForm, city: e.target.value })
                  }
                  disabled={!isEditing}
                  className={`flex-1 min-w-0 px-2 py-2 rounded-lg border text-sm ${
                    isEditing
                      ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                      : "border-gray-200 bg-gray-50 text-gray-600"
                  }`}
                />
                <input
                  type="text"
                  value={editForm.state}
                  onChange={e =>
                    setEditForm({ ...editForm, state: e.target.value })
                  }
                  disabled={!isEditing}
                  maxLength={2}
                  className={`w-12 shrink-0 px-1 py-2 rounded-lg border text-sm text-center ${
                    isEditing
                      ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                      : "border-gray-200 bg-gray-50 text-gray-600"
                  }`}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500 block mb-1">CEP</span>
              <input
                type="text"
                value={editForm.zip}
                onChange={e => handleZipChange(e.target.value)}
                disabled={!isEditing}
                maxLength={9}
                placeholder="00000-000"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isEditing
                    ? "border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              />
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">Zona</span>
              {isEditing ? (
                <select
                  value={editForm.zone_id}
                  onChange={e =>
                    setEditForm({ ...editForm, zone_id: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-lg border text-sm border-[#0085BD] bg-white focus:ring-1 focus:ring-[#0085BD]"
                >
                  <option value="">Sem zona</option>
                  {zones
                    .filter(zone => zone.status === "A")
                    .map(zone => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                </select>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: costumer.zone?.color || "#ccc" }}
                  />
                  <span className="text-sm text-gray-600">
                    {costumer.zone?.name || "Sem zona"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="group relative bg-gradient-to-br from-[#0085BD]/10 to-[#006a96]/5 p-4 rounded-xl text-center border border-[#0085BD]/20 hover:border-[#0085BD]/40 transition-all duration-200">
            <p className="text-2xl font-bold bg-gradient-to-r from-[#0085BD] to-[#006a96] bg-clip-text text-transparent">
              {costumer.total_reports}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">Total relatórios</p>
          </div>
          <div className={`group relative p-4 rounded-xl text-center border transition-all duration-200 ${
            costumer.days_without_report === null || costumer.days_without_report > 45
              ? "bg-gradient-to-br from-red-500/10 to-rose-500/5 border-red-500/20 hover:border-red-500/40"
              : "bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-500/20 hover:border-green-500/40"
          }`}>
            <p className={`text-2xl font-bold bg-clip-text text-transparent ${
              costumer.days_without_report === null || costumer.days_without_report > 45
                ? "bg-gradient-to-r from-red-600 to-rose-600"
                : "bg-gradient-to-r from-green-600 to-emerald-600"
            }`}>
              {costumer.days_without_report ?? "-"}
            </p>
            <p className="text-xs text-gray-500 font-medium mt-1">Dias sem relatório</p>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="mt-4 flex flex-col gap-2">
          {costumer.last_report_id && (
            <a
              href={`/tech-visit-reports/pdf/${costumer.last_report_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#0085BD] text-white py-2.5 rounded-lg hover:bg-[#006a96] transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <FiFileText size={18} />
              PDF Último Relatório
            </a>
          )}
          {/* Botão Navegação GPS */}
          {costumer.latitude && costumer.longitude && (
            <button
              type="button"
              onClick={() => {
                const lat = costumer.latitude;
                const lng = costumer.longitude;
                const address = encodeURIComponent(
                  `${costumer.address}, ${costumer.number} - ${costumer.district}, ${costumer.city} - ${costumer.state}`,
                );

                // Detecta se é mobile
                const isMobileDevice =
                  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent,
                  );

                if (isMobileDevice) {
                  // Tenta abrir Waze primeiro, se falhar abre Google Maps
                  const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
                  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;

                  // Cria um menu de escolha simples
                  if (
                    window.confirm(
                      "Abrir com Waze?\n\nClique OK para Waze ou Cancelar para Google Maps",
                    )
                  ) {
                    window.open(wazeUrl, "_blank");
                  } else {
                    window.open(googleMapsUrl, "_blank");
                  }
                } else {
                  // Desktop: abre Google Maps
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
                    "_blank",
                  );
                }
              }}
              className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2.5 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
            >
              <FiNavigation size={18} />
              <span>Navegar até o Cliente</span>
              <FiExternalLink size={14} className="opacity-70" />
            </button>
          )}
        </div>
      </div>

      {/* Lista de Responsáveis (colapsável) */}
      <div className="border-b">
        <button
          type="button"
          onClick={() => setShowResponsibles(!showResponsibles)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h3 className="font-medium text-gray-700 flex items-center gap-2">
            <FiUsers className="text-[#0085BD]" />
            Responsáveis
            <span className="text-sm text-gray-400">
              ({responsibles.length})
            </span>
          </h3>
          {showResponsibles ? (
            <FiChevronUp className="text-gray-400" />
          ) : (
            <FiChevronDown className="text-gray-400" />
          )}
        </button>

        {showResponsibles && (
          <div className="px-4 pb-4">
            {loadingResponsibles && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0085BD] mx-auto" />
              </div>
            )}
            {!loadingResponsibles && responsibles.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                Nenhum responsável cadastrado
              </p>
            )}
            {!loadingResponsibles && responsibles.length > 0 && (
              <div className="space-y-2">
                {responsibles.map(resp => (
                  <div
                    key={resp.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                  >
                    <p className="font-medium text-gray-700 text-sm">
                      {resp.name}
                    </p>
                    <div className="mt-1 space-y-1">
                      {resp.email ? (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <FiMail size={12} /> {resp.email}
                        </p>
                      ) : null}
                      {resp.phone ? (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <FiPhone size={12} /> {resp.phone}
                        </p>
                      ) : null}
                      {!resp.email && !resp.phone && (
                        <p className="text-xs text-gray-400 italic">N/A</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Observações/Emergência (colapsável) */}
      <div className="border-b">
        <button
          type="button"
          onClick={() => setShowObservations(!showObservations)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h3 className="font-medium text-gray-700 flex items-center gap-2">
            <FiMessageSquare className="text-[#0085BD]" />
            Observações
            <span className="text-sm text-gray-400">
              ({observations.length})
            </span>
          </h3>
          {showObservations ? (
            <FiChevronUp className="text-gray-400" />
          ) : (
            <FiChevronDown className="text-gray-400" />
          )}
        </button>

        {showObservations && (
          <div className="px-4 pb-4">
            {/* Nova Observação */}
            <div className="mb-4">
              <textarea
                value={newObservation}
                onChange={e => setNewObservation(e.target.value)}
                placeholder="Descreva a situação de emergência..."
                className="w-full border rounded-lg p-3 text-sm resize-none h-20 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
              />
              <button
                type="button"
                onClick={handleCreateObservation}
                disabled={submitting || !newObservation.trim()}
                className="mt-2 w-full bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                <FiAlertTriangle />
                {submitting ? "Salvando..." : "Registrar Emergência"}
              </button>
            </div>

            {/* Lista de Observações */}
            {loading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0085BD] mx-auto" />
              </div>
            )}
            {!loading && observations.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                Nenhuma observação registrada
              </p>
            )}
            {!loading && observations.length > 0 && (
              <div className="space-y-2">
                {observations.map(obs => (
                  <div
                    key={obs.id}
                    className={`border rounded-lg p-3 ${
                      obs.status === "open"
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-gray-700">{obs.text}</p>
                      {obs.status === "open" && (
                        <button
                          type="button"
                          onClick={() => openCloseConfirmation(obs.id)}
                          className="shrink-0 p-1 bg-green-500 text-white rounded hover:bg-green-600"
                          title="Resolver"
                        >
                          <FiCheck size={14} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{obs.user.name}</span>
                      <span>
                        {new Date(obs.opened_at).toLocaleDateString("pt-BR")}
                        {obs.status === "closed" && obs.closed_at && (
                          <span className="ml-1 text-green-600">
                            ✓{" "}
                            {new Date(obs.closed_at).toLocaleDateString(
                              "pt-BR",
                            )}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmação para fechar observação */}
      <Modal
        isOpen={confirmCloseModal.isOpen}
        onClose={() =>
          setConfirmCloseModal({ isOpen: false, observationId: "" })
        }
        title="Fechar Observação Emergencial"
        icon={<FiAlertOctagon size={24} />}
        variant="warning"
        footer={
          <>
            <ModalButton
              variant="cancel"
              onClick={() =>
                setConfirmCloseModal({ isOpen: false, observationId: "" })
              }
            >
              Cancelar
            </ModalButton>
            <ModalButton
              variant="success"
              onClick={handleCloseObservation}
              disabled={closingObservation}
            >
              {closingObservation ? "Fechando..." : "Confirmar"}
            </ModalButton>
          </>
        }
      >
        <p className="text-gray-700">
          Tem certeza que deseja fechar esta observação emergencial?
        </p>
        {observations.filter(obs => obs.status === "open").length === 1 && (
          <p className="mt-3 text-amber-600 font-medium">
            <FiAlertOctagon className="inline mr-1" />
            Esta é a última observação aberta. O cliente deixará de ser marcado
            como emergência.
          </p>
        )}
      </Modal>
    </div>
    </>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prettier/prettier */

// Função auxiliar para validar coordenadas (fora do componente para evitar recriação)
const isValidCoordinate = (lat: number, lng: number): boolean => {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat !== 0 &&
    lng !== 0 &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

// Função para obter cor baseada nos dias sem relatório
const getMonitoringColor = (daysWithoutReport: number | null): string => {
  if (daysWithoutReport === null) return "#EF4444"; // Crítico para sem relatório
  if (daysWithoutReport <= 15) return "#22C55E"; // Verde
  if (daysWithoutReport <= 30) return "#EAB308"; // Amarelo
  if (daysWithoutReport <= 45) return "#F97316"; // Laranja
  return "#EF4444"; // Vermelho
};

function MapComponent({
  costumers,
  selectedZones,
  selectedStatuses,
  viewMode,
  pinTheme,
  mapBackground,
  reportType,
  onCostumerClick,
}: MapComponentProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const tileLayerRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const onCostumerClickRef = useRef(onCostumerClick);
  const isFirstRenderRef = useRef(true);

  // Mantém a referência da função atualizada
  useEffect(() => {
    onCostumerClickRef.current = onCostumerClick;
  }, [onCostumerClick]);

  // Filtra clientes baseado nas zonas selecionadas, status de monitoramento e se tem coordenadas válidas
  const filteredCostumers = costumers
    .filter(costumer =>
      isValidCoordinate(costumer.latitude, costumer.longitude),
    )
    .filter(
      costumer =>
        selectedZones.length === 0 ||
        selectedZones.includes(costumer.zone?.id || ""),
    )
    .filter(
      costumer =>
        selectedStatuses.length === 0 ||
        selectedStatuses.includes(getMonitoringStatus(costumer)),
    );

  // Carrega o Leaflet dinamicamente
  useEffect(() => {
    if ((window as any).L) {
      setIsLoaded(true);
      return;
    }

    // Carrega CSS do Leaflet
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    // Carrega JS do Leaflet
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);

    // Adiciona estilos customizados para os tooltips
    const customStyles = document.createElement("style");
    customStyles.innerHTML = `
      .leaflet-tooltip.custom-tooltip {
        background: transparent !important;
        border: none !important;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
        padding: 0 !important;
        border-radius: 12px !important;
        opacity: 1 !important;
      }
      .leaflet-tooltip.custom-tooltip::before {
        display: none !important;
      }
    `;
    document.head.appendChild(customStyles);
  }, []);

  // Inicializa o mapa quando Leaflet estiver carregado
  useEffect(() => {
    if (
      isLoaded &&
      mapRef.current &&
      !mapInstanceRef.current &&
      (window as any).L
    ) {
      const defaultCenter: [number, number] = [-23.5505, -46.6333];

      mapInstanceRef.current = (window as any).L.map(mapRef.current).setView(
        defaultCenter,
        10,
      );

      // Adiciona tile layer inicial
      const tileUrl =
        mapBackground === "satellite"
          ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

      const attribution =
        mapBackground === "satellite"
          ? '&copy; <a href="https://www.esri.com/">Esri</a>'
          : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

      tileLayerRef.current = (window as any).L.tileLayer(tileUrl, {
        attribution,
      }).addTo(mapInstanceRef.current);
    }
  }, [isLoaded, mapBackground]);

  // Atualiza tile layer quando o fundo muda
  useEffect(() => {
    if (mapInstanceRef.current && tileLayerRef.current && (window as any).L) {
      // Remove tile layer anterior
      mapInstanceRef.current.removeLayer(tileLayerRef.current);

      // Adiciona novo tile layer
      const tileUrl =
        mapBackground === "satellite"
          ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

      const attribution =
        mapBackground === "satellite"
          ? '&copy; <a href="https://www.esri.com/">Esri</a>'
          : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

      tileLayerRef.current = (window as any).L.tileLayer(tileUrl, {
        attribution,
      }).addTo(mapInstanceRef.current);
    }
  }, [mapBackground]);

  // Atualiza marcadores quando filtros mudam
  useEffect(() => {
    if (mapInstanceRef.current && (window as any).L) {
      // Remove marcadores existentes
      markersRef.current.forEach(marker => {
        mapInstanceRef.current.removeLayer(marker);
      });
      markersRef.current = [];

      // Adiciona novos marcadores
      filteredCostumers.forEach(costumer => {
        if (!isValidCoordinate(costumer.latitude, costumer.longitude)) {
          return;
        }

        // Determina a cor baseada no modo de visualização
        let color: string;

        if (costumer.is_delivery_only && reportType !== "delivery") {
          // Cliente só tem relatórios de entrega e não está no filtro de entrega - pin cinza
          color = "#9CA3AF";
        } else if (viewMode === "monitoring") {
          color = getMonitoringColor(costumer.days_without_report);
        } else {
          color = costumer.zone?.color || "#000000";
        }

        // Valor dos dias para exibir no pin
        // Se é delivery_only, só mostra dias quando reportType for "delivery"
        const daysValue =
          (costumer.is_delivery_only && reportType !== "delivery") || costumer.days_without_report === null
            ? ""
            : costumer.days_without_report;

        // Nome truncado para o pin (máx 15 caracteres)
        const shortName =
          costumer.name.length > 15
            ? `${costumer.name.substring(0, 15)}...`
            : costumer.name;

        // Calcula cor de contraste para o texto
        const getContrastColor = (hexColor: string) => {
          const r = parseInt(hexColor.slice(1, 3), 16);
          const g = parseInt(hexColor.slice(3, 5), 16);
          const b = parseInt(hexColor.slice(5, 7), 16);
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          return luminance > 0.5 ? '#1f2937' : '#ffffff';
        };

        const textColor = getContrastColor(color);

        // Função para gerar HTML do pin baseado no tema
        const getPinHtml = (isEmergency: boolean) => {
          // Indicador de emergência (badge com !)
          const emergencyBadge = isEmergency
            ? `
              <div style="
                position: absolute;
                top: -2px;
                right: -2px;
                width: 14px;
                height: 14px;
                background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
                border-radius: 50%;
                border: 1.5px solid white;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 4px rgba(239, 68, 68, 0.5);
                z-index: 10;
              ">
                <span style="color: white; font-size: 9px; font-weight: bold;">!</span>
              </div>
            `
            : "";

          // Anel pulsante para emergência (apenas no pin gota)
          const emergencyRing = isEmergency
            ? `
              <div style="
                position: absolute;
                inset: -3px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 2px solid #EF4444;
                animation: pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              "></div>
              <style>
                @keyframes pulse-ring {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.4; }
                }
              </style>
            `
            : "";

          if (pinTheme === "number") {
            // Pin moderno estilo drop/gota com número
            return `
              <div style="
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.25));
              ">
                <div style="
                  position: relative;
                  width: 28px;
                  height: 28px;
                ">
                  ${emergencyRing}
                  ${emergencyBadge}
                  <div style="
                    width: 28px;
                    height: 28px;
                    background: linear-gradient(145deg, ${color} 0%, ${color}dd 100%);
                    border-radius: 50% 50% 50% 0;
                    transform: rotate(-45deg);
                    border: 2px solid rgba(255,255,255,0.9);
                    box-shadow:
                      inset 0 2px 3px rgba(255,255,255,0.3),
                      inset 0 -2px 3px rgba(0,0,0,0.1);
                  ">
                    <div style="
                      position: absolute;
                      inset: 0;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      transform: rotate(45deg);
                      font-size: 11px;
                      font-weight: 700;
                      color: ${textColor};
                      text-shadow: 0 1px 1px rgba(0,0,0,0.2);
                    ">
                      ${daysValue}
                    </div>
                  </div>
                </div>
              </div>
            `;
          }

          if (pinTheme === "name") {
            // Pin com número + nome em badge elegante
            return `
              <div style="
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 3px;
                filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.2));
              ">
                <div style="
                  position: relative;
                  width: 26px;
                  height: 26px;
                ">
                  ${emergencyRing}
                  ${emergencyBadge}
                  <div style="
                    width: 26px;
                    height: 26px;
                    background: linear-gradient(145deg, ${color} 0%, ${color}dd 100%);
                    border-radius: 50% 50% 50% 0;
                    transform: rotate(-45deg);
                    border: 2px solid rgba(255,255,255,0.95);
                    box-shadow:
                      inset 0 2px 3px rgba(255,255,255,0.3),
                      inset 0 -2px 3px rgba(0,0,0,0.1);
                  ">
                    <div style="
                      position: absolute;
                      inset: 0;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      transform: rotate(45deg);
                      font-size: 10px;
                      font-weight: 700;
                      color: ${textColor};
                      text-shadow: 0 1px 1px rgba(0,0,0,0.2);
                    ">
                      ${daysValue}
                    </div>
                  </div>
                </div>
                <div style="
                  background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%);
                  padding: 2px 6px;
                  border-radius: 8px;
                  font-size: 9px;
                  font-weight: 600;
                  color: #374151;
                  white-space: nowrap;
                  max-width: 80px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  box-shadow:
                    0 2px 4px rgba(0,0,0,0.15),
                    0 1px 2px rgba(0,0,0,0.1);
                  border: 1px solid rgba(0,0,0,0.05);
                ">
                  ${shortName}
                </div>
              </div>
            `;
          }

          if (pinTheme === "fullName") {
            // Pin com nome completo em card mais elaborado
            return `
              <div style="
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 3px;
                filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2));
              ">
                <div style="
                  position: relative;
                  width: 26px;
                  height: 26px;
                ">
                  ${emergencyRing}
                  ${emergencyBadge}
                  <div style="
                    width: 26px;
                    height: 26px;
                    background: linear-gradient(145deg, ${color} 0%, ${color}dd 100%);
                    border-radius: 50% 50% 50% 0;
                    transform: rotate(-45deg);
                    border: 2px solid rgba(255,255,255,0.95);
                    box-shadow:
                      inset 0 2px 3px rgba(255,255,255,0.3),
                      inset 0 -2px 3px rgba(0,0,0,0.1);
                  ">
                    <div style="
                      position: absolute;
                      inset: 0;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      transform: rotate(45deg);
                      font-size: 10px;
                      font-weight: 700;
                      color: ${textColor};
                      text-shadow: 0 1px 1px rgba(0,0,0,0.2);
                    ">
                      ${daysValue}
                    </div>
                  </div>
                </div>
                <div style="
                  background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%);
                  padding: 3px 8px;
                  border-radius: 10px;
                  font-size: 10px;
                  font-weight: 600;
                  color: #1f2937;
                  white-space: nowrap;
                  box-shadow:
                    0 2px 6px rgba(0,0,0,0.15),
                    0 1px 2px rgba(0,0,0,0.1);
                  border: 1px solid rgba(0,0,0,0.05);
                  letter-spacing: -0.2px;
                ">
                  ${costumer.name}
                </div>
              </div>
            `;
          }

          // Fallback para número (não deveria acontecer)
          return "";
        };

        // Cria o marcador
        const iconHtml = getPinHtml(costumer.emergency);

        // Determina tamanho e âncora baseado no tema
        let iconSize: [number, number] = [36, 36];
        let iconAnchor: [number, number] = [18, 36];
        if (pinTheme === "name") {
          iconSize = [90, 50];
          iconAnchor = [45, 32];
        }
        if (pinTheme === "fullName") {
          iconSize = [200, 55];
          iconAnchor = [100, 32];
        }

        const icon = (window as any).L.divIcon({
          html: iconHtml,
          className: "",
          iconSize,
          iconAnchor,
        });
        const marker = (window as any).L.marker(
          [costumer.latitude, costumer.longitude],
          { icon },
        );

        // Configura tooltip (aparece ao passar o mouse) ao invés de popup
        const zoneColorBadge = costumer.zone?.color || '#6B7280';
        const tooltipDaysColor = (costumer.is_delivery_only && reportType !== "delivery")
          ? '#9CA3AF'
          : getMonitoringColor(costumer.days_without_report);

        marker.bindTooltip(
          `
          <div style="
            padding: 0;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            min-width: 240px;
            border-radius: 12px;
            overflow: hidden;
            background: white;
          ">
            <div style="
              background: linear-gradient(135deg, #0085BD 0%, #006a96 100%);
              padding: 12px 14px;
            ">
              <h3 style="
                margin: 0;
                color: white;
                font-size: 15px;
                font-weight: 600;
                line-height: 1.3;
              ">
                ${costumer.name}
              </h3>
              ${
                costumer.emergency
                  ? `<div style="
                      display: flex;
                      align-items: center;
                      gap: 6px;
                      margin-top: 8px;
                      background: rgba(239, 68, 68, 0.9);
                      color: white;
                      padding: 4px 10px;
                      border-radius: 6px;
                      font-size: 11px;
                      font-weight: 600;
                    ">
                      <span style="font-size: 12px;">⚠</span>
                      <span>EMERGÊNCIA ATIVA</span>
                    </div>`
                  : ""
              }
            </div>
            <div style="padding: 12px 14px;">
              <div style="
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 8px;
              ">
                <span style="
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                  background: ${zoneColorBadge};
                  flex-shrink: 0;
                "></span>
                <span style="
                  font-size: 12px;
                  color: #374151;
                  font-weight: 500;
                ">${costumer.zone?.name || "Sem zona"}</span>
              </div>
              <p style="
                margin: 0 0 6px 0;
                font-size: 12px;
                color: #6B7280;
                line-height: 1.4;
              ">
                📍 ${costumer.address}, ${costumer.number}<br/>
                ${costumer.district} - ${costumer.city}/${costumer.state}
              </p>
              <div style="
                display: flex;
                gap: 12px;
                margin-top: 10px;
                padding-top: 10px;
                border-top: 1px solid #E5E7EB;
              ">
                <div style="flex: 1; text-align: center;">
                  <div style="
                    font-size: 18px;
                    font-weight: 700;
                    color: ${tooltipDaysColor};
                  ">${costumer.days_without_report ?? "-"}</div>
                  <div style="font-size: 10px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.3px;">Dias s/ rel.</div>
                </div>
                <div style="flex: 1; text-align: center;">
                  <div style="
                    font-size: 18px;
                    font-weight: 700;
                    color: #0085BD;
                  ">${costumer.total_reports}</div>
                  <div style="font-size: 10px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.3px;">Relatórios</div>
                </div>
              </div>
              ${
                costumer.is_delivery_only
                  ? `<div style="
                      margin-top: 10px;
                      padding: 6px 10px;
                      background: #F3F4F6;
                      border-radius: 8px;
                      font-size: 11px;
                      color: #6B7280;
                      text-align: center;
                    ">📦 Apenas entregas</div>`
                  : ""
              }
            </div>
          </div>
        `,
          {
            sticky: true,
            className: 'custom-tooltip',
            offset: [0, 20],
            direction: 'bottom',
          },
        );

        // Clique no marcador abre o painel lateral e dá zoom in
        marker.on("click", () => {
          // Zoom in no marcador clicado
          mapInstanceRef.current.setView(
            [costumer.latitude, costumer.longitude],
            15, // nível de zoom mais próximo
            { animate: true },
          );

          if (onCostumerClickRef.current) {
            onCostumerClickRef.current(costumer);
          }
        });

        marker.addTo(mapInstanceRef.current);
        markersRef.current.push(marker);
      });

      // Ajusta zoom se há marcadores válidos (apenas na primeira renderização)
      if (markersRef.current.length > 0 && isFirstRenderRef.current) {
        const FeatureGroup = (window as any).L.featureGroup;
        const group = new FeatureGroup(markersRef.current);
        mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1));
        isFirstRenderRef.current = false;
      }
    }
  }, [filteredCostumers, viewMode, pinTheme, reportType]);

  if (!isLoaded) {
    return (
      <div
        style={{
          height: "600px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>Carregando mapa...</div>
      </div>
    );
  }

  return <div ref={mapRef} style={{ height: "600px", width: "100%" }} />;
}

export function CostumersMap() {
  const { hasPermission } = useAuth();

  // Determinar permissões e tipo padrão
  const canViewTechnicalVisit = hasPermission("tech-visit-reports:view-technical-visit");
  const canViewDelivery = hasPermission("tech-visit-reports:view-delivery");
  const hasBothPermissions = canViewTechnicalVisit && canViewDelivery;

  // Definir tipo padrão baseado nas permissões
  const getDefaultType = (): "technical_visit" | "delivery" => {
    if (canViewTechnicalVisit && !canViewDelivery) return "technical_visit";
    if (canViewDelivery && !canViewTechnicalVisit) return "delivery";
    return "technical_visit"; // Padrão se tiver ambas ou nenhuma
  };

  const [reportType, setReportType] = useState<"technical_visit" | "delivery">(getDefaultType());
  const [costumers, setCostumers] = useState<Costumer[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<MonitoringStatus[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("monitoring");
  const [pinTheme, setPinTheme] = useState<PinTheme>("number");
  const [mapBackground, setMapBackground] = useState<MapBackground>("streets");
  const [selectedCostumerId, setSelectedCostumerId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedCostumer, setSelectedCostumer] = useState<Costumer | null>(
    null,
  );
  const [isMobile, setIsMobile] = useState(false);
  const [zonesDropdownOpen, setZonesDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const zonesDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Detecta se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fecha dropdown de zonas ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        zonesDropdownRef.current &&
        !zonesDropdownRef.current.contains(event.target as Node)
      ) {
        setZonesDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadData = async (type?: string) => {
    try {
      setDataLoading(true);
      const [costumersResponse, zonesResponse] = await Promise.all([
        api.get("/costumers/map", { params: { type: type || reportType } }),
        api.get("/zones"),
      ]);

      setCostumers(costumersResponse.data);
      setZones(zonesResponse.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadData(reportType);
    }
  }, [reportType]);

  const handleGeocodeAll = async () => {
    try {
      await api.post("/costumers/geocode/all");
      // eslint-disable-next-line no-alert
      alert(
        "Processo de geocodificação iniciado! Aguarde alguns minutos e recarregue a página.",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao iniciar geocodificação:", error);
    }
  };

  // Handler para quando uma observação é criada/fechada
  const handleObservationCreated = () => {
    // Recarrega os dados para atualizar o flag de emergência
    loadData();
  };

  // Handler para clique no cliente no mapa
  const handleCostumerClick = (costumer: Costumer) => {
    setSelectedCostumer(costumer);
  };

  // Filtro de clientes por busca e modo de emergência
  const filteredCostumersBySearch = costumers
    .filter(c => selectedCostumerId === "" || c.id === selectedCostumerId)
    .filter(c => viewMode !== "emergency" || c.emergency === true);

  // Big Numbers - Cálculos
  const validCostumers = costumers.filter(c =>
    isValidCoordinate(c.latitude, c.longitude),
  );
  const totalActiveClients = validCostumers.length;
  const deliveryOnlyClients = validCostumers.filter(
    c => c.is_delivery_only,
  ).length;
  const emergencyClients = validCostumers.filter(c => c.emergency).length;
  const criticalClients = validCostumers.filter(
    c =>
      !c.is_delivery_only &&
      (c.days_without_report === null || c.days_without_report > 45),
  ).length;

  if (loading) {
    return (
      <div className="container mx-auto">
        <PageTitle title="Mapa de Clientes" backButton backPath="/home" />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0085BD]" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <PageTitle title="Mapa de Clientes" backButton backPath="/home" />

      {/* Big Numbers */}
      <BigNumbersSection
        totalActiveClients={totalActiveClients}
        deliveryOnlyClients={deliveryOnlyClients}
        emergencyClients={emergencyClients}
        criticalClients={criticalClients}
        dataLoading={dataLoading}
      />

      {/* Toggle de Tipo de Relatório - Mostrar apenas se tiver ambas permissões */}
      {hasBothPermissions && (
        <ReportTypeToggle
          reportType={reportType}
          dataLoading={dataLoading}
          onReportTypeChange={setReportType}
        />
      )}

      {/* Controles */}
      <FilterControls
        selectedCostumerId={selectedCostumerId}
        onCostumerIdChange={setSelectedCostumerId}
        zones={zones}
        selectedZones={selectedZones}
        onZonesChange={setSelectedZones}
        zonesDropdownOpen={zonesDropdownOpen}
        onZonesDropdownToggle={setZonesDropdownOpen}
        zonesDropdownRef={zonesDropdownRef}
        selectedStatuses={selectedStatuses}
        onStatusesChange={setSelectedStatuses}
        statusDropdownOpen={statusDropdownOpen}
        onStatusDropdownToggle={setStatusDropdownOpen}
        statusDropdownRef={statusDropdownRef}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        pinTheme={pinTheme}
        onPinThemeChange={setPinTheme}
        mapBackground={mapBackground}
        onMapBackgroundChange={setMapBackground}
        onGeocodeAll={handleGeocodeAll}
        dataLoading={dataLoading}
      />

      {/* Mapa e Painel */}
      <div className={`${isMobile ? "flex flex-col" : "flex flex-row gap-4"} transition-opacity duration-300 ${
        dataLoading ? 'opacity-50' : 'opacity-100'
      }`}>
        {/* Mapa */}
        <div
          className={`bg-white p-4 rounded-md ${
            isMobile || !selectedCostumer ? "w-full" : "flex-1"
          }`}
        >
          <MapComponent
            costumers={filteredCostumersBySearch}
            selectedZones={selectedZones}
            selectedStatuses={selectedStatuses}
            viewMode={viewMode}
            pinTheme={pinTheme}
            mapBackground={mapBackground}
            reportType={reportType}
            onCostumerClick={handleCostumerClick}
          />
        </div>

        {/* Painel no Desktop (lado direito) */}
        {selectedCostumer && !isMobile && (
          <SidePanel
            costumer={selectedCostumer}
            zones={zones}
            onClose={() => setSelectedCostumer(null)}
            onObservationCreated={handleObservationCreated}
            onCostumerUpdated={loadData}
          />
        )}

        {/* Painel no Mobile (abaixo do mapa) */}
        {selectedCostumer && isMobile && (
          <div className="mt-4">
            <SidePanel
              costumer={selectedCostumer}
              zones={zones}
              onClose={() => setSelectedCostumer(null)}
              onObservationCreated={handleObservationCreated}
              onCostumerUpdated={loadData}
              isMobile
            />
          </div>
        )}
      </div>

      {/* Legenda Modernizada */}
      <MapLegend zones={zones} />
    </div>
  );
}
